<template>
  <div :class="colStartClass">
    <template v-if="crudItems.length > 1">
      <!-- Stacked permissions: show master toggle with gear icon -->
      <div v-if="!shouldShowExpanded" class="group cursor-pointer pb-2">
        <div class="flex flex-row items-center gap-2 group-hover:text-primary-600">
          <InfuseToggle
            :id="`master-${accessType}-${crudRoot.objectType}`"
            :value="areAllSelected"
            @on="selectAll"
            @off="deselectAll"
          />
          <font-awesome-icon
            :icon="['fas', 'cog']"
            class="text-neutral-400 hover:text-primary-500"
            size="sm"
            @click.stop="toggleExpanded"
          />
        </div>
      </div>
      <!-- Expanded view: show all individual toggles -->
      <template v-else>
        <div v-for="crudItem in crudItems" :key="crudItem.id" class="group cursor-pointer pb-2">
          <div class="flex flex-row items-center gap-2 group-hover:text-primary-600">
            <InfuseToggle
              :id="`${crudItem.id}}`"
              :value="enabledPermissions.includes(crudItem.id)"
              @on="addPermission(crudItem.id)"
              @off="removePermission(crudItem.id)"
            />
            <font-awesome-icon
              v-if="crudItem === crudItems[0]"
              :icon="['fas', 'cog']"
              :class="
                !areAllSelected && !areAllUnselected
                  ? 'cursor-not-allowed text-neutral-200'
                  : 'cursor-pointer text-primary-500 hover:text-primary-600'
              "
              size="sm"
              @click.stop="() => (!areAllSelected && !areAllUnselected ? null : toggleExpanded())"
            />
          </div>

          <div
            class="flex flex-col gap-0.5 text-xs text-neutral-500 group-hover:text-primary-500"
            @click="togglePermission(crudItem.id)"
          >
            <span>
              {{ crudItem.name }}

              <InfuseTooltip :content="crudItem.description" class="inline-block cursor-help text-primary-500">
                <font-awesome-icon :icon="['fal', 'circle-info']" />
              </InfuseTooltip>
            </span>
            <span v-if="crudItem.permissionId.endsWith('legacy')" class="italic text-warning-300">Legacy</span>
          </div>
        </div>
      </template>
    </template>
    <!-- Single permission: show normal toggle -->
    <template v-else>
      <div v-for="crudItem in crudItems" :key="crudItem.id" class="group cursor-pointer pb-2">
        <div class="flex flex-row items-center gap-2 group-hover:text-primary-600">
          <InfuseToggle
            :id="`${crudItem.id}}`"
            :value="enabledPermissions.includes(crudItem.id)"
            @on="addPermission(crudItem.id)"
            @off="removePermission(crudItem.id)"
          />
          <InfuseTooltip :content="crudItem.description" class="cursor-help text-primary-500">
            <font-awesome-icon :icon="['fal', 'circle-info']" size="sm" />
          </InfuseTooltip>
        </div>

        <div
          class="flex flex-col gap-0.5 text-xs text-neutral-500 group-hover:text-primary-500"
          @click="togglePermission(crudItem.id)"
        >
          <span v-if="crudItem.permissionId.endsWith('legacy')" class="italic text-warning-300">Legacy</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { AccessType } from '@infuse/api/iam/permission';
import { InfuseToggle, InfuseTooltip } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  accessType: AccessType;
  colStartClass: string;
  crudRoot: any;
  enabledPermissions: string[];
  addPermission: (permissionId: string) => void;
  removePermission: (permissionId: string) => void;
}>();

const isExpanded = ref(false);

const crudItems = computed(() => {
  return props.crudRoot.crud.filter((c: any) => c.accessType === props.accessType);
});

const areAllSelected = computed(() => {
  if (crudItems.value.length === 0) {
    return false;
  }
  return crudItems.value.every((item: any) => props.enabledPermissions.includes(item.id));
});

const areAllUnselected = computed(() => {
  if (crudItems.value.length === 0) {
    return false;
  }
  return crudItems.value.every((item: any) => !props.enabledPermissions.includes(item.id));
});

const hasGranularChoices = computed(() => {
  if (crudItems.value.length === 0) {
    return false;
  }
  const selectedCount = crudItems.value.filter((item: any) => props.enabledPermissions.includes(item.id)).length;
  return selectedCount > 0 && selectedCount < crudItems.value.length;
});

const shouldShowExpanded = computed(() => {
  return isExpanded.value || hasGranularChoices.value;
});

// Auto-expand when granular choices are made
watch(
  hasGranularChoices,
  (newValue) => {
    if (newValue) {
      isExpanded.value = true;
    }
  },
  { immediate: true }
);

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

function togglePermission(permissionId: string) {
  if (props.enabledPermissions.includes(permissionId)) {
    props.removePermission(permissionId);
  } else {
    props.addPermission(permissionId);
  }
}

function selectAll() {
  crudItems.value.forEach((item: any) => {
    if (!props.enabledPermissions.includes(item.id)) {
      props.addPermission(item.id);
    }
  });
}

function deselectAll() {
  crudItems.value.forEach((item: any) => {
    if (props.enabledPermissions.includes(item.id)) {
      props.removePermission(item.id);
    }
  });
}
</script>
